// Iter 83 — Extracted from TradeDoctor.jsx (was lines ~1485-1620).
// "Fix These First" — severity-sorted leak cards. Each card surfaces
// Title, ₹/$ Impact, Why, Action, and a CTA button (inert placeholder
// per the iter-82 spec — fires a "coming soon" toast).
import React from "react";
import { AlertOctagon, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";

export function FixTheseFirstCards({ leaks, testid }) {
  const severityRank = { high: 0, moderate: 1, low: 2 };
  const sorted = (Array.isArray(leaks) ? leaks : [])
    .slice()
    .sort(
      (a, b) =>
        (severityRank[a?.impact] ?? 3) - (severityRank[b?.impact] ?? 3),
    );
  if (sorted.length === 0) {
    return (
      <div
        data-testid={testid}
        className="bt-card border border-[var(--bt-border)] bg-[var(--bt-surface)] rounded-sm p-5"
      >
        <div className="flex items-center gap-2 mb-2">
          <AlertOctagon className="w-5 h-5" style={{ color: "var(--bt-loss)" }} />
          <h3 className="font-heading text-base">Fix These First</h3>
        </div>
        <p className="text-sm text-[var(--bt-text-muted)]">
          No major leaks flagged.
        </p>
      </div>
    );
  }
  return (
    <div data-testid={testid} className="space-y-3">
      <div className="flex items-center gap-2">
        <AlertOctagon className="w-5 h-5" style={{ color: "var(--bt-loss)" }} />
        <h3 className="font-heading text-base">Fix These First</h3>
        <span className="text-xs text-[var(--bt-text-muted)] ml-auto">
          {sorted.length} {sorted.length === 1 ? "leak" : "leaks"} · highest severity first
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {sorted.map((l, i) => (
          <FixTheseFirstCard key={i} leak={l} index={i} />
        ))}
      </div>
    </div>
  );
}

export function FixTheseFirstCard({ leak, index }) {
  const sev = (leak?.impact || "").toLowerCase();
  const sevColor =
    sev === "high" ? "var(--bt-loss)"
      : sev === "moderate" ? "var(--bt-warn)"
      : "var(--bt-text-muted)";
  // Iter 82 — fall back gracefully to the existing `detail` text when the
  // LLM (cached audit / older response) didn't fill the new structured
  // fields. Keeps backward compatibility with iter-81 audits.
  const why = (leak?.why || "").trim() || (leak?.detail || "").trim();
  const action = (leak?.action || "").trim();
  const impactAmount = (leak?.impact_amount || "").trim();
  const challengeLabel = (leak?.challenge_label || "").trim() || "Start Challenge";

  return (
    <div
      data-testid={`leak-card-${index}`}
      className="rounded-sm border p-4 space-y-2"
      style={{
        borderColor: `color-mix(in srgb, ${sevColor} 45%, var(--bt-border))`,
        background: `color-mix(in srgb, ${sevColor} 4%, var(--bt-surface))`,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <h4
          className="font-heading text-sm leading-snug min-w-0 line-clamp-2"
          title={leak?.title || ""}
        >
          {leak?.title || "Unnamed leak"}
        </h4>
        {sev && (
          <span
            data-testid={`leak-card-${index}-severity`}
            className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
            style={{
              color: sevColor,
              background: `color-mix(in srgb, ${sevColor} 12%, transparent)`,
            }}
          >
            {sev}
          </span>
        )}
      </div>

      {impactAmount && (
        <div
          data-testid={`leak-card-${index}-impact`}
          className="font-mono text-xs"
          style={{ color: sevColor }}
        >
          Impact: {impactAmount}
        </div>
      )}

      {why && (
        <p
          data-testid={`leak-card-${index}-why`}
          className="text-xs leading-relaxed"
          style={{ color: "var(--bt-text-muted)" }}
        >
          <span className="font-semibold" style={{ color: "var(--bt-text)" }}>Why it matters: </span>
          {why}
        </p>
      )}

      {action && (
        <p
          data-testid={`leak-card-${index}-action`}
          className="text-xs leading-relaxed"
        >
          <span className="font-semibold">Action: </span>
          <span style={{ color: "var(--bt-text)" }}>{action}</span>
        </p>
      )}

      <div className="pt-2">
        <Button
          data-testid={`leak-card-${index}-cta`}
          size="sm"
          variant="outline"
          onClick={() =>
            toast.info(
              `${challengeLabel} — challenge tracking coming soon. We've noted your intent to commit to this fix.`,
            )
          }
          className="text-xs border-[var(--bt-border)] hover:bg-[var(--bt-surface-2)]"
          style={{ color: sevColor, borderColor: `color-mix(in srgb, ${sevColor} 35%, var(--bt-border))` }}
        >
          <Sparkles className="w-3 h-3 mr-1.5" />
          {challengeLabel}
        </Button>
      </div>
    </div>
  );
}
